import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import products, { categories } from '../data/products';
import ProductCard from '../components/products/ProductCard';
import { SearchIcon } from '../components/common/Icons';
import './Products.css';

const levenshteinDistance = (s1, s2) => {
  const track = Array(s2.length + 1).fill(null).map(() =>
    Array(s1.length + 1).fill(null));
  for (let i = 0; i <= s1.length; i += 1) track[0][i] = i;
  for (let j = 0; j <= s2.length; j += 1) track[j][0] = j;
  for (let j = 1; j <= s2.length; j += 1) {
    for (let i = 1; i <= s1.length; i += 1) {
      const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1, // deletion
        track[j - 1][i] + 1, // insertion
        track[j - 1][i - 1] + indicator, // substitution
      );
    }
  }
  return track[s2.length][s1.length];
};

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesCategory = activeCategory === 'All' || p.category === activeCategory;
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchTerm]);

  // Suggestion logic for "Do you mean"
  const getSuggestions = () => {
    if (!searchTerm) return [];
    const query = searchTerm.toLowerCase();

    // Score all products based on similarity to search term
    const scoredProducts = products.map(p => {
      const name = p.name.toLowerCase();
      let score = 0;

      // 1. Check for word overlaps
      const queryWords = query.split(/\s+/).filter(w => w.length > 1);
      const nameWords = name.split(/\s+/);

      queryWords.forEach(qw => {
        if (nameWords.includes(qw)) score += 20;
        else if (name.includes(qw)) score += 10;
      });

      // 2. Levenshtein distance similarity
      const distance = levenshteinDistance(query, name);
      const maxLength = Math.max(query.length, name.length);
      const similarity = 1 - (distance / maxLength);

      score += similarity * 50;

      return { name: p.name, score };
    });

    // Return products sorted by score, above a certain threshold
    return scoredProducts
      .filter(p => p.score > 15)
      .sort((a, b) => b.score - a.score)
      .map(p => p.name);
  };

  const allSuggestions = getSuggestions();
  const suggestions = allSuggestions.slice(0, 3);
  const bestMatch = allSuggestions[0];

  return (
    <div className="products-page">
      <section className="page-header">
        <div className="container">
          <h1>Our Product Catalogue</h1>
          <p>Explore our wide range of industrial shuttering and scaffolding solutions.</p>
        </div>
      </section>

      <section className="section">
        <div className="container">

          {/* Search and Filter Bar */}
          <div className="catalog-controls">
            <div className="search-container">
              <div className="search-bar">
                <SearchIcon size={20} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search among products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="btn btn-primary search-btn">Search</button>
            </div>

            <div className="category-filter">
              <button
                className={`filter-btn ${activeCategory === 'All' ? 'active' : ''}`}
                onClick={() => setActiveCategory('All')}
              >
                All Products
              </button>
              {categories.map((cat, index) => (
                <button
                  key={index}
                  className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid grid-3 product-grid">
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))
            ) : (
              <div className="no-products text-center">
                <div className="no-product-image">
                  <img src="/Product images/no product.png" alt="No products found" />
                </div>
                <h3>Sorry! There are no results for "{searchTerm}"</h3>

                {bestMatch && searchTerm.length > 2 && (
                  <p className="suggestion-text mt-20">
                    Do you mean <span className="suggestion-link" onClick={() => setSearchTerm(bestMatch)}>{bestMatch}</span>?
                  </p>
                )}

                {suggestions.length > 0 && (
                  <div className="suggestions-list mt-20">
                    <p>Try searching for:</p>
                    {suggestions.map((name, index) => (
                      <div
                        key={index}
                        className="suggestion-item"
                        onClick={() => setSearchTerm(name)}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bulk Order CTA */}
      <section className="section bulk-cta bg-light">
        <div className="container text-center">
          <h3>Need a Bulk Quote?</h3>
          <p>We offer competitive pricing for large-scale industrial orders. Contact our sales team for a customized proposal.</p>
          <Link to="/contact" className="btn btn-primary mt-20">Get Bulk Quote</Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
